export const FIRST_NAME_SLUG = "p_first_name";
export const LAST_NAME_SLUG = "p_last_name";
export const EMAIL_SLUG = "p_email";
export const PHONE_SLUG = "p_phone";

export type SeedField = { slug: string; fieldType: string; isRequired: boolean };
export type SeedBuyer = { firstName?: string; lastName?: string; email?: string; phone?: string };
export type ParticipantEntry = {
  id?: string;
  isSelf?: boolean;
  answers: Record<string, unknown>;
  linkedAnswers?: Record<string, unknown>;
  /**
   * The ticket type this seat was bought under. It belongs to the POSITION,
   * not to the person: moving someone between seats moves their details and
   * leaves each seat's ticket where it is. Absent when the flow has no ticket
   * groups (a bundle, an appointment).
   */
  ticketTypeId?: string;
};

/** A group of seats bought under one ticket type (see ticket-seat-groups.ts). */
export type SeatGroup = { ticketTypeId: string; seatCount: number };

function isBlankAnswer(v: unknown): boolean {
  return (
    v === "" ||
    v === null ||
    v === undefined ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0)
  );
}

/**
 * An unfilled attendee slot: a plain inline entry (no saved id, not the buyer)
 * whose answers are all blank. Empty slots are allowed at checkout (lenient) —
 * they represent a paid seat with no named attendee yet.
 */
export function isSlotEmpty(entry: ParticipantEntry): boolean {
  if (entry.id || entry.isSelf) return false;
  return Object.values(entry.answers).every(isBlankAnswer);
}

/**
 * Force the attendee list to exactly `n` slots (one per purchased seat). Short
 * lists are padded with empty inline slots; over-long lists shed trailing EMPTY
 * slots first, then hard-cap (only losing filled data if the seat count was
 * reduced mid-checkout). Returns the SAME reference when already `n` long so a
 * driving effect converges instead of looping.
 */
export function padToSlots(value: ParticipantEntry[], n: number): ParticipantEntry[] {
  if (value.length === n) return value;
  if (value.length < n) {
    const out = value.slice();
    while (out.length < n) out.push({ answers: {} });
    return out;
  }
  const out = value.slice();
  while (out.length > n && isSlotEmpty(out[out.length - 1]!)) out.pop();
  if (out.length > n) out.length = n;
  return out;
}

/** The person in a seat, without the seat's own ticket. */
function slotContent(entry: ParticipantEntry): ParticipantEntry {
  const { ticketTypeId: _seatTicket, ...content } = entry;
  return content;
}

/** Put `content` in a seat, keeping the seat's ticket. */
function inSeat(seat: ParticipantEntry, content: ParticipantEntry): ParticipantEntry {
  return seat.ticketTypeId ? { ...slotContent(content), ticketTypeId: seat.ticketTypeId } : slotContent(content);
}

/**
 * Set whether the buyer attends, keeping the list exactly `n` slots (or the
 * ticket groups' layout when given). The buyer takes the first empty seat, so
 * the people already named keep their seats and tickets; turning off frees
 * the buyer's seat back to an empty one in place. The "You" card renders
 * above the list wherever the buyer sits.
 */
export function setBuyerSlot(
  value: ParticipantEntry[],
  attending: boolean,
  seedAnswers: Record<string, unknown>,
  n: number,
  groups?: readonly SeatGroup[],
): ParticipantEntry[] {
  const pad = (next: ParticipantEntry[]) =>
    groups?.length ? padToGroups(next, groups) : padToSlots(next, n);
  const selfIndex = value.findIndex((p) => p.isSelf);
  if (!attending) {
    if (selfIndex < 0) return pad(value);
    return pad(value.map((p, i) => (i === selfIndex ? inSeat(p, { answers: {} }) : p)));
  }
  if (selfIndex >= 0) return pad(value);
  const buyer: ParticipantEntry = { isSelf: true, answers: seedAnswers };
  const laidOut = pad(value);
  const empty = laidOut.findIndex((p) => isSlotEmpty(p));
  if (empty >= 0) return laidOut.map((p, i) => (i === empty ? inSeat(p, buyer) : p));
  // Every seat is taken (the step disables the toggle then): the buyer goes
  // first and the last seat's occupant loses their place, as before.
  return pad([buyer, ...laidOut]);
}

/**
 * Move the buyer into a seat of another ticket type ("I'm on the teen
 * ticket"). Takes the first empty seat of that ticket, else trades places with
 * the first person on it, who moves to the buyer's old seat.
 */
export function moveBuyerToTicket(value: ParticipantEntry[], ticketTypeId: string): ParticipantEntry[] {
  const from = value.findIndex((p) => p.isSelf);
  if (from < 0 || value[from]!.ticketTypeId === ticketTypeId) return value;
  const seats = value.map((p, i) => i).filter((i) => value[i]!.ticketTypeId === ticketTypeId);
  if (!seats.length) return value;
  const to = seats.find((i) => isSlotEmpty(value[i]!)) ?? seats[0]!;
  const out = value.slice();
  out[to] = inSeat(value[to]!, value[from]!);
  out[from] = inSeat(value[from]!, value[to]!);
  return out;
}

/**
 * Lay the list out as the ticket groups' seats, in group order, each slot
 * carrying its group's ticket. People stay on the ticket they were entered
 * under; when a group shrinks it sheds empty seats first, and anyone left
 * without a seat (their ticket's quantity went down, or the list predates the
 * groups) moves to the first empty seat of any group. Only when the whole
 * order shrank below the people named does anyone drop off, as with
 * `padToSlots`. Returns the SAME reference when nothing changes, so a driving
 * effect converges.
 */
export function padToGroups(value: ParticipantEntry[], groups: readonly SeatGroup[]): ParticipantEntry[] {
  const known = new Set(groups.map((g) => g.ticketTypeId));
  const buckets = new Map<string, ParticipantEntry[]>(groups.map((g) => [g.ticketTypeId, []]));
  const homeless: ParticipantEntry[] = [];
  for (const p of value) {
    if (p.ticketTypeId && known.has(p.ticketTypeId)) buckets.get(p.ticketTypeId)!.push(p);
    else if (!isSlotEmpty(p)) homeless.push(p);
  }
  const laid = groups.map((g) => {
    const own = buckets.get(g.ticketTypeId)!;
    // Shed empty seats first (from the end), then people beyond the count.
    while (own.length > g.seatCount) {
      let k = own.length - 1;
      while (k >= 0 && !isSlotEmpty(own[k]!)) k--;
      if (k < 0) break;
      own.splice(k, 1);
    }
    while (own.length > g.seatCount) homeless.push(own.pop()!);
    while (own.length < g.seatCount) own.push({ answers: {}, ticketTypeId: g.ticketTypeId });
    return own;
  });
  // Seat the people who lost theirs in the first empty seats, in order.
  for (const own of laid) {
    for (let k = 0; k < own.length && homeless.length; k++) {
      if (isSlotEmpty(own[k]!)) own[k] = homeless.shift()!;
    }
  }
  const next = laid.flatMap((own, gi) =>
    own.map((p) => (p.ticketTypeId === groups[gi]!.ticketTypeId ? p : { ...slotContent(p), ticketTypeId: groups[gi]!.ticketTypeId })),
  );
  if (next.length === value.length && next.every((p, i) => p === value[i])) return value;
  return next;
}

/** Count the filled seats — the buyer, any saved attendee, or a filled slot. */
export function filledSlotCount(value: ParticipantEntry[]): number {
  return value.filter((p) => !isSlotEmpty(p)).length;
}

/**
 * Map the buyer's entered identity onto the school's attendee fields for the
 * buyer-as-attendee seed. Built-in slugs map 1:1. When the school archived the
 * built-in name fields and asks a custom text field instead (e.g. one "Name"),
 * seed the buyer's full name into the first text-type field — required fields
 * first, then field order — mirroring the API's fallback display-name rule
 * (duplicated across the bundler boundary; keep the two in sync).
 */
export function buildBuyerSeedAnswers(fields: SeedField[], buyer: SeedBuyer): Record<string, unknown> {
  const has = (slug: string) => fields.some((f) => f.slug === slug);
  const answers: Record<string, unknown> = {};
  if (has(FIRST_NAME_SLUG) && buyer.firstName) answers[FIRST_NAME_SLUG] = buyer.firstName;
  if (has(LAST_NAME_SLUG) && buyer.lastName) answers[LAST_NAME_SLUG] = buyer.lastName;
  if (has(EMAIL_SLUG) && buyer.email) answers[EMAIL_SLUG] = buyer.email;
  if (has(PHONE_SLUG) && buyer.phone) answers[PHONE_SLUG] = buyer.phone;
  if (!(FIRST_NAME_SLUG in answers) && !(LAST_NAME_SLUG in answers)) {
    const fullName = [buyer.firstName, buyer.lastName]
      .filter((v): v is string => typeof v === "string" && v.trim() !== "")
      .join(" ");
    const candidates = fields.filter(
      (f) => f.fieldType === "text" && f.slug !== FIRST_NAME_SLUG && f.slug !== LAST_NAME_SLUG,
    );
    const target = candidates.find((f) => f.isRequired) ?? candidates[0];
    if (target && fullName) answers[target.slug] = fullName;
  }
  return answers;
}


/**
 * Mirror of the API's pair map (CustomerField.service.ts BUILT_IN_LINKED_SLUGS).
 * Duplicated across the bundler boundary — keep the two identical.
 */
export const BUILT_IN_LINKED_SLUGS: Readonly<Record<string, string>> = {
  [FIRST_NAME_SLUG]: "first_name",
  [LAST_NAME_SLUG]: "last_name",
  [EMAIL_SLUG]: "email",
  [PHONE_SLUG]: "phone",
};

/** Targets resolved from the customer's own columns rather than a custom field. */
const IDENTITY_TARGETS = new Set([...Object.values(BUILT_IN_LINKED_SLUGS), "full_name"]);

export type LinkableField = { slug: string; linkedCustomerSlug?: string | null };

/** The customer field this question reads through, or null when it is the attendee's own. */
export function resolveLinkedCustomerSlugClient(field: LinkableField): string | null {
  return field.linkedCustomerSlug || BUILT_IN_LINKED_SLUGS[field.slug] || null;
}

/**
 * Fields the self attendee record STORES: the attendee's own, never a linked
 * one. A linked value lives on the customer record and is read through, so the
 * dashboard renders these as the editable set and the server strips the rest
 * from a self write.
 */
export function fieldsForSelf<T extends LinkableField>(fields: T[]): T[] {
  return fields.filter((f) => resolveLinkedCustomerSlugClient(f) === null);
}

/**
 * Fields to ASK the buyer on their own card at checkout. Wider than what the
 * record stores: a question linked to a CUSTOM customer field is asked here —
 * once, on the attendee step — and routed to the customer record, which is the
 * whole point of linking. Only identity targets are left off, because the
 * buyer's name and contact come from the buyer block of the order.
 */
export function fieldsForSelfCard<T extends LinkableField>(fields: T[]): T[] {
  return fields.filter((f) => {
    const target = resolveLinkedCustomerSlugClient(f);
    return target === null || !IDENTITY_TARGETS.has(target);
  });
}

/**
 * The buyer's answers to LINKED custom questions, keyed by customer slug, so
 * the basket page can show them as already collected instead of asking again.
 * Presentation only — the server recomputes this from the self entry, so a
 * client that skips it still produces a correct order.
 */
export function linkedAnswersFor(
  fields: LinkableField[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const target = resolveLinkedCustomerSlugClient(f);
    if (!target || IDENTITY_TARGETS.has(target)) continue;
    const v = answers[f.slug];
    if (v === undefined || v === null || v === "") continue;
    out[target] = v;
  }
  return out;
}

/**
 * Opening answers for the buyer's card. What they have already typed elsewhere
 * in this basket wins over their saved record, so answering once covers every
 * listing. Linked fields are never seeded — they aren't on the card at all.
 */
export function buildSelfSeedAnswers(
  fields: LinkableField[],
  savedSelfValues: Record<string, string> | undefined,
  shared: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  // The card's set, not the record's: a linked custom question is asked here,
  // and the saved self record carries its read-through value to seed it with.
  for (const f of fieldsForSelfCard(fields)) {
    const v = shared?.[f.slug] ?? savedSelfValues?.[f.slug];
    if (v !== undefined && v !== null && v !== "") out[f.slug] = v;
  }
  return out;
}
